import React, { useCallback, useState } from "react";
import styles from "./styles.module.scss";
import ModalLayoutWithTitle from "app/components/Layouts/ModalLayoutWithTitle";
import Button from "app/components/Button/ButtonVariant";
import SearchBar from "app/components/inputs/SearchBar";
import UserModel from "app/models/UserModel";
import { useActions } from "app/utils/hooks";
import {
  addMembersToTeam,
  fetchMembersForSelect,
  fetchTeamGroups,
} from "app/actions/team-group";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useDebounce from "app/utils/hooks/useDebounce";
import { usePagination } from "app/utils/hooks/usePagination";
import { inviteModalType } from "app/actions/team-group-member-import";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import MembersList from "../MembersList";

type QueryType = {
  page: number;
  perPage: number;
  userName?: string;
};

const PER_PAGE = 20;
const INIT_PAGE = 1;

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  onMemberInvite?: (members: UserModel[]) => void;
}

const TeamsMemberInviteModal = ({ isOpen, onClose, onMemberInvite }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const inviteActions = useActions({ fetchMembersForSelect, addMembersToTeam });
  const { error } = useSelector(
    (state: RootState) => state.teamGroup,
    shallowEqual,
  );
  const [selectedMembers, setSelectedMembers] = useState<UserModel[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const { id, teamId } = useParams<{ id: string; teamId: string }>();
  const [updatingMembers, setUpdatingMembers] = useState(false);

  const debouncedSearch = useDebounce(searchValue, 300);

  const INIT_QUERY: QueryType = {
    page: INIT_PAGE,
    perPage: PER_PAGE,
    userName: debouncedSearch?.length > 2 ? debouncedSearch : undefined,
  };

  const {
    data: membersList,
    pending: loading,
    sentryRef,
    loadInitialData,
  } = usePagination<QueryType, UserModel>({
    initialParams: INIT_QUERY,
    fetchData: async (params, signal) => {
      const res = await inviteActions.fetchMembersForSelect(
        teamId,
        params,
        signal,
      );
      return res || [];
    },
    clearDataAction: () => {
      setSelectedMembers([]);
    },
    dependencies: [debouncedSearch, teamId],
  });

  const onSearchChange = useCallback((search?: string) => {
    setSearchValue(search);
  }, []);

  const onSelectHandler = useCallback(
    (key: string, value: boolean) => {
      setSelectedMembers((prev) => {
        if (value) {
          const selected = membersList.find((m: UserModel) => m.id === key);
          if (selected && !prev.some((m) => m.id === key)) {
            return [...prev, selected];
          }
        } else {
          return prev.filter((m) => m.id !== key);
        }
        return prev;
      });
    },
    [membersList],
  );

  const onSubmitHandler = useCallback(() => {
    if (selectedMembers.length === 0) {
      return;
    }
    setUpdatingMembers(true);

    inviteActions
      .addMembersToTeam(
        teamId,
        selectedMembers.map((m) => m.id),
      )
      .then((res) => {
        if (onMemberInvite) {
          onMemberInvite(res);
        }
        dispatch(fetchTeamGroups(id));
        loadInitialData(INIT_QUERY);
        onClose();
      })
      .finally(() => setUpdatingMembers(false));
  }, [selectedMembers, teamId, inviteActions, onClose, onMemberInvite]);

  const onInviteGroupMembers = () => {
    dispatch(inviteModalType("invite-members"));
  };

  return (
    <ModalLayoutWithTitle
      isOpen={isOpen}
      onClose={!updatingMembers && onClose}
      headerTitle={t("Add Members to Channel")}
    >
      <div className={styles.container}>
        <div className={styles.content_wrapper}>
          <SearchBar
            className={styles.search_input}
            placeholder={t("Search Members")}
            onChange={onSearchChange}
          />
          <MembersList
            membersList={membersList}
            onSelectHandler={onSelectHandler}
            selectedMembers={selectedMembers}
            sentryRef={sentryRef}
            pending={loading}
          />
          {membersList?.length === 0 && !loading && (
            <div className={styles.null_list_container}>
              {error?.originalErrors ? (
                <span className={styles.error}>
                  {t("Something went wrong")}
                </span>
              ) : (
                <span>{t("No members found")}</span>
              )}
              <Button
                title={t("Invite Members to Group")}
                buttonType="primary"
                onClickFunc={onInviteGroupMembers}
              />
            </div>
          )}
        </div>
        <div className={styles.button_wrapper}>
          <Button
            buttonType="secondary-outline"
            title={t("Skip")}
            onClickFunc={onClose}
            disabled={loading || updatingMembers}
          />
          <Button
            buttonType="primary"
            title={t("Add Selected")}
            onClickFunc={onSubmitHandler}
            disabled={
              selectedMembers?.length === 0 || loading || updatingMembers
            }
          />
        </div>
      </div>
    </ModalLayoutWithTitle>
  );
};

export default TeamsMemberInviteModal;
